import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import { removeDuplicates } from 'utils/removeDuplicates';

import errorIcon from 'assets/icons/error-icon.svg';
import trashIcon from 'assets/icons/trash.svg';
import loadingIcon from 'assets/icons/spinner.svg';
import closeIcon from 'assets/icons/close.svg';
import checkCircleIcon from 'assets/icons/check-circle.svg';
import hourGlassIcon from 'assets/icons/whitehourglass-icon.svg';
import emailIcon from 'assets/icons/email-icon-w.svg';

import {
  StyledToast,
  StyledIconAction,
  StyledButton,
  StyledCloseBtn,
  StyledToastContainer,
  StyledToastSeparator,
  StyledToastColumn,
} from './styled';

const Toast = props => {
  const { toastList: sourceList = [], autoDelete, dismissTime, onDismiss = undefined } = props;

  const [list, setList] = useState([]);

  useEffect(() => {
    const filtered = removeDuplicates(sourceList || [], 'id');
    setList(filtered);
  }, [sourceList]);

  const removeToasts = useCallback(
    ids => {
      setList(prev => prev.filter(t => !ids.has(t.id)));
      onDismiss?.(ids);
    },
    [onDismiss],
  );

  const deleteToast = useCallback(id => removeToasts(new Set([id])), [removeToasts]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!list.length) return;

      const now = Date.now();

      const expiredIds = list
        .filter(toast => {
          if (toast.type === 'loading') return false;

          const shouldAutoDelete = toast.autoDelete !== undefined ? toast.autoDelete : autoDelete;
          if (!shouldAutoDelete) return false;

          const createdAt = toast.createdAt ?? 0;
          return now - createdAt >= dismissTime;
        })
        .map(toast => toast.id);

      if (!expiredIds.length) return;

      removeToasts(new Set(expiredIds));
    }, dismissTime);

    return () => clearInterval(interval);
  }, [list, autoDelete, dismissTime, removeToasts]);

  const handleIconByType = type => {
    switch (type) {
      case 'emailConfirmation':
        return emailIcon;
      case 'info':
        return trashIcon;
      case 'loading':
        return loadingIcon;
      case 'success':
        return checkCircleIcon;
      case 'darkSuccess':
        return checkCircleIcon;
      case 'warning':
        return null;
      case 'error':
        return errorIcon;
      case 'trash':
        return trashIcon;
      case 'updating':
        return hourGlassIcon;
      default:
        return null;
    }
  };

  return (
    <StyledToastContainer>
      {list.map((toast, i) => (
        <StyledToast key={toast.id || i} theme={toast.type}>
          <StyledToastColumn>
            {handleIconByType(toast.type) && (
              <StyledIconAction src={handleIconByType(toast.type)} alt={toast.type} type={toast.type} />
            )}
            <p>{toast.message}</p>
          </StyledToastColumn>

          <StyledToastSeparator />

          <StyledToastColumn>
            {toast.btnTitle && <StyledButton onClick={toast.onClick}>{toast.btnTitle}</StyledButton>}

            {toast.id !== 'outbox-pending' && (
              <StyledCloseBtn src={closeIcon} alt={toast.type} onClick={() => deleteToast(toast.id)} />
            )}
          </StyledToastColumn>
        </StyledToast>
      ))}
    </StyledToastContainer>
  );
};

Toast.propTypes = {
  toastList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      message: PropTypes.string,
      btnTitle: PropTypes.string,
      onClick: PropTypes.func,
    }),
  ),
  autoDelete: PropTypes.bool,
  dismissTime: PropTypes.number,
  onDismiss: PropTypes.func,
};

export { Toast };
